import { memo, useCallback } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import { CalendarIcon, PinIcon } from '@/assets'
import { useAnalytics, CART_TYPE } from '@/core/analytics'
import { useTranslations } from '@/core/translation'
import { formatDate, ROUTES, usePackageInfo } from '@/utils'
import useUpdatePackageInfo from '@/utils/hooks/useUpdatePackageInfo'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { summarySidebarStyles as styles } from '../SummarySidebar/SummarySidebar.style'
import { SummaryCardActions } from '@/components/Composed'
import { globalClasses } from '@/styles'
import { useRemoveHotelMutation } from '@/redux/package'
import { useToast } from '@/components'

const HOTEL_DATE_FORMAT = 'MMM DD'
const HotelSummary = () => {
  const t = useTranslations()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { currentPackage, isHotelModify, isSingleProduct } = usePackageInfo()
  const [removeHotel] = useRemoveHotelMutation()
  const { refetchPackageInfo } = useUpdatePackageInfo()

  const analytics = useAnalytics()

  const { sessionId } = currentPackage ?? { sessionId: '' }

  const onHotelEditClick = useCallback(() => {
    navigate({
      pathname: ROUTES.PACKAGE.HOTEL_RESULTS,
      search: createSearchParams({
        sessionId,
        modify: 'true',
        redirectUrl: location.pathname,
      }).toString(),
    })
  }, [sessionId, navigate, location.pathname])

  const onHotelRemoveClick = useCallback(async () => {
    try {
      await removeHotel({ sessionId }).unwrap()
      refetchPackageInfo(sessionId)
      analytics.sendRemoveFromCartEvent(CART_TYPE.HOTEL)
    } catch {
      toast(t('general.apiError'), 'error')
    }
  }, [removeHotel, sessionId, refetchPackageInfo, analytics, toast, t])

  if (!currentPackage?.hotelInfo || !currentPackage.hotelParams) return null

  const {
    hotelInfo: { name, roomAndRateInfo, address },
  } = currentPackage

  const { checkIn, checkOut, rooms } =
    currentPackage.cartInfo.hotel?.searchParams ?? currentPackage.hotelParams

  const numberOfRooms = rooms?.length ?? 0

  return (
    <Box sx={styles.addOnCard}>
      <Box>
        <Text sx={styles.addOnTitle}>{name}</Text>
        <Text sx={styles.tickets}>
          <Text component="span" sx={styles.numberOfRooms}>
            x
          </Text>
          {`${numberOfRooms} ${roomAndRateInfo.name.toLowerCase()}`}
        </Text>
        <Box sx={styles.locationWrapper}>
          <Box sx={styles.pinIcon}>
            <PinIcon />
          </Box>
          <Text
            sx={styles.bookDesc}
            className={globalClasses.ellipsis}
            title={`${address.line1}, ${address.city}`}>
            {address.line1.toLowerCase()}, {address.city.toLowerCase()}
          </Text>
        </Box>
        <Box sx={styles.checkInWrapper}>
          <Box sx={styles.calendarWrapper}>
            <CalendarIcon />
          </Box>

          <Box sx={styles.checkInDetailsWrapper}>
            <Box className="flex">
              <Text sx={styles.checkIn}>{t('hotel.search.checkIn')}:</Text>
              <Text sx={styles.checkInDate}>{formatDate(checkIn, HOTEL_DATE_FORMAT)}</Text>
            </Box>
            <Text sx={styles.dashLine}>-</Text>
            <Box className="flex">
              <Text sx={styles.checkIn}>{t('hotel.search.checkOut')}:</Text>
              <Text sx={styles.checkInDate}>{formatDate(checkOut, HOTEL_DATE_FORMAT)}</Text>
            </Box>
          </Box>
        </Box>
      </Box>
      <SummaryCardActions
        onEditClick={onHotelEditClick}
        onRemoveClick={onHotelRemoveClick}
        isActionDisabled={isHotelModify}
        isRemoveActionDisabled={isSingleProduct}
      />
    </Box>
  )
}

export default memo(HotelSummary)
